import { Brand } from '@/components/Brand';
import { CasinoButton } from '@/components/CasinoButton';
import { Flaming7 } from '@/components/Flaming7';
import { HarlequinBackdrop } from '@/components/HarlequinBackdrop';
import { LedNumber } from '@/components/LedNumber';
import { LeaderboardRow } from '@/components/LeaderboardRow';
import { TournamentCard } from '@/components/TournamentCard';
import { Button } from '@/components/ui/button';
import { palette } from '@/theme';

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-3">
    <header>
      <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
        {title}
      </h2>
      {subtitle ? <p className="text-cabinet-cream/60 text-sm">{subtitle}</p> : null}
    </header>
    <div>{children}</div>
  </section>
);

const Swatch = ({ name, value }: { name: string; value: string }) => (
  <div className="flex items-center gap-3 rounded-lg bg-black/30 p-2 ring-1 ring-cabinet-cream/10">
    <div
      className="h-10 w-10 shrink-0 rounded-md ring-1 ring-black/60"
      style={{ background: value }}
    />
    <div className="min-w-0">
      <p className="font-display text-cabinet-cream text-sm leading-tight">{name}</p>
      <p className="font-mono text-[11px] text-cabinet-cream/60">{value}</p>
    </div>
  </div>
);

export function StyleGuide() {
  const samplePayouts = [
    { place: 1, pct: 50 },
    { place: 2, pct: 35 },
    { place: 3, pct: 15 },
  ];

  return (
    <main className="mx-auto max-w-5xl px-5 py-10 space-y-12">
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <HarlequinBackdrop className="px-6 py-10 md:px-12 md:py-14">
        <div className="flex flex-col items-center text-center gap-4">
          <div className="flex items-center gap-4">
            <Flaming7 size={120} />
            <Flaming7 size={170} />
            <Flaming7 size={120} />
          </div>
          <Brand size="xl" />
          <p className="text-cabinet-cream/80 max-w-md">
            Phase 1 — visual review surface. Palette, typography, components.
          </p>
        </div>
      </HarlequinBackdrop>

      {/* ── Palette ────────────────────────────────────────────────────── */}
      <Section title="Palette" subtitle="Sourced from the cabinet topper and belly glass.">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <Swatch name="Purple" value={palette.purple.base} />
          <Swatch name="Purple deep" value={palette.purple.deep} />
          <Swatch name="Purple ink" value={palette.purple.ink} />
          <Swatch name="Royal blue" value={palette.blue.base} />
          <Swatch name="Gold" value={palette.gold.base} />
          <Swatch name="Gold bright" value={palette.gold.bright} />
          <Swatch name="Gold deep" value={palette.gold.deep} />
          <Swatch name="Cream" value={palette.cream} />
          <Swatch name="Flame orange" value={palette.flame.orange} />
          <Swatch name="Flame red" value={palette.flame.red} />
          <Swatch name="Flame ember" value={palette.flame.ember} />
          <Swatch name="Chrome" value={palette.chrome.base} />
          <Swatch name="Chrome light" value={palette.chrome.light} />
          <Swatch name="Chrome dark" value={palette.chrome.dark} />
          <Swatch name="LED on" value={palette.led.on} />
          <Swatch name="LED dim" value={palette.led.dim} />
        </div>
      </Section>

      {/* ── Typography ─────────────────────────────────────────────────── */}
      <Section title="Typography" subtitle="Display / body / LED.">
        <div className="cabinet-card p-6 space-y-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50 mb-2">
              Display — gold marquee
            </p>
            <p className="text-marquee font-display text-6xl uppercase">JACKPOT</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50 mb-2">
              Display — red marquee
            </p>
            <p className="text-marquee-red font-display text-5xl uppercase">Tournament</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50 mb-2">
              Body — Inter
            </p>
            <p className="text-base text-cabinet-cream">
              The quick brown fox jumps over the lazy dog. 1,304 credits remaining.
            </p>
            <p className="text-sm text-cabinet-cream/70 mt-1">
              Secondary line — used for metadata and helper copy.
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/50 mb-2">
              LED — DSEG7 7-segment
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <LedNumber value={1304} digits={5} size="sm" />
              <LedNumber value={1304} digits={5} />
              <LedNumber value={1304} digits={5} currency size="xl" pulse />
            </div>
            <p className="text-xs text-cabinet-cream/50 mt-2">
              If the LED above looks like a normal monospace, run{' '}
              <code className="rounded bg-black/40 px-1.5 py-0.5">npm run fetch-fonts</code>{' '}
              once.
            </p>
          </div>
        </div>
      </Section>

      {/* ── Buttons ────────────────────────────────────────────────────── */}
      <Section title="Buttons" subtitle="Chunky chrome-bordered casino buttons.">
        <div className="cabinet-card p-6 space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <CasinoButton>Submit</CasinoButton>
            <CasinoButton variant="red">Play</CasinoButton>
            <CasinoButton variant="ghost">Cancel</CasinoButton>
            <CasinoButton disabled>Disabled</CasinoButton>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button>Default</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
          <CasinoButton fullWidth variant="red">
            Join Tournament — Full Width
          </CasinoButton>
        </div>
      </Section>

      {/* ── LED display close-up ───────────────────────────────────────── */}
      <Section title="LED display" subtitle="Used for balances, scores, and pots.">
        <div className="cabinet-card p-6">
          <div className="chrome-bezel">
            <div className="rounded-[0.625rem] bg-black p-4 flex flex-col items-center gap-3">
              <p className="text-[10px] uppercase tracking-widest text-cabinet-cream/60">
                Credits
              </p>
              <LedNumber value={1304} digits={6} currency size="xl" pulse />
              <p className="font-display text-cabinet-gold-bright tracking-wider uppercase text-sm">
                Win Paid 25¢
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Tournament card ────────────────────────────────────────────── */}
      <Section title="Tournament card" subtitle="Hero element on the player dashboard.">
        <div className="max-w-md">
          <TournamentCard
            name="Spring 2026 Open"
            entryFeeCents={2500}
            potCents={50000}
            payouts={samplePayouts}
          />
        </div>
      </Section>

      {/* ── Leaderboard rows ───────────────────────────────────────────── */}
      <Section title="Leaderboard rows" subtitle="Live, SSE-driven. Your row glows gold.">
        <div className="cabinet-card p-3 space-y-2 max-w-md">
          <LeaderboardRow rank={1} name="Joshua P." score={4815} paying />
          <LeaderboardRow rank={2} name="Lucas P." score={4202} paying />
          <LeaderboardRow rank={3} name="Sara P." score={3960} paying />
          <LeaderboardRow rank={4} name="Craig (Papa)" score={3804} isMe pending />
          <LeaderboardRow rank={5} name="Gretchen" score={3120} />
          <LeaderboardRow rank={6} name="Dyanne" score={2880} />
        </div>
      </Section>

      {/* ── Harlequin pattern reference ────────────────────────────────── */}
      <Section title="Harlequin pattern" subtitle="Echoes the purple cabinet topper.">
        <div className="grid gap-3 md:grid-cols-2">
          <HarlequinBackdrop className="h-32" />
          <HarlequinBackdrop variant="bright" className="h-32" />
        </div>
      </Section>

      <footer className="pt-4 text-center text-xs text-cabinet-cream/40">
        Slot Tournament · style-guide · Phase 1
      </footer>
    </main>
  );
}
